import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS, fmtDateTime } from "@/lib/checkins";
import { StatusChip } from "../StatusCard";
import { errMsg } from "./ResolveDialog";

function OverrideDialog({ sid, domain, onClose, onDone }) {
  const [status, setStatus] = useState(domain.status);
  const [reason, setReason] = useState("");
  const [clientText, setClientText] = useState("");
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api.post(`/checkin-submissions/${sid}/override`, { domain_code: domain.code, status, reason, client_text: clientText || null });
      toast.success("Override recorded — original result kept in the audit trail");
      onDone();
      onClose();
    } catch (e) {
      toast.error("Override failed", { description: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkin-override-dialog">
        <DialogHeader>
          <DialogTitle>Override {domain.label}</DialogTitle>
          <DialogDescription>The automated result ({STATUS[domain.computed_status || domain.status]?.label}) is preserved with your reason, name and time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>New status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="checkin-override-status"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Clinical reason (staff only)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="checkin-override-reason" /></div>
          <div><Label>Wording for the client summary (optional)</Label><Textarea rows={2} value={clientText} onChange={(e) => setClientText(e.target.value)} data-testid="checkin-override-client-text" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || reason.trim().length < 3} data-testid="checkin-override-save">Save override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DomainResults({ sub, readOnly, onChanged }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-2" data-testid="checkin-domain-results">
      {sub.domain_results.map((d) => (
        <div key={d.code} className="rounded-lg border border-zinc-200 p-3" data-testid={`checkin-domain-${d.code}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-zinc-950">{d.label}</span>
            <StatusChip status={d.status} small testId={`checkin-domain-${d.code}-status`} />
            {d.safety_override && <span className="text-2xs font-semibold text-red-700">Safety override</span>}
            {d.overridden_by && <span className="text-2xs font-semibold text-sky-800">Practitioner override (was {STATUS[d.computed_status]?.label})</span>}
            {!readOnly && <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => setEditing(d)} data-testid={`checkin-domain-override-${d.code}`}>Override</Button>}
          </div>
          {d.dose && <p className="mt-1 font-mono-num text-xs text-zinc-600">{d.dose}</p>}
          <p className="mt-1 text-sm text-zinc-700">{d.reason}</p>
        </div>
      ))}
      {sub.overrides?.length > 0 && (
        <div className="rounded-lg bg-zinc-50 p-3 text-xs text-zinc-700" data-testid="checkin-override-history">
          <div className="font-semibold text-zinc-900">Override history</div>
          {sub.overrides.map((o) => (
            <div key={o.id}>{fmtDateTime(o.at)} · {o.by_name}: {o.domain_code} {STATUS[o.original_status]?.label} → {STATUS[o.new_status]?.label} — {o.reason}</div>
          ))}
        </div>
      )}
      {editing && <OverrideDialog sid={sub.id} domain={editing} onClose={() => setEditing(null)} onDone={onChanged} />}
    </div>
  );
}
